import type { ReportFreshness, ReportQueryRequest } from '@/api/types';

export type ReportMetricFamily = 'delivery' | 'attribution' | 'roas';

export type ReportMetricAvailabilityStatus =
  | 'unknown'
  | 'unsupported'
  | 'unpublished'
  | 'published';

export interface ReportMetricAvailability {
  delivery: ReportMetricAvailabilityStatus;
  attribution: ReportMetricAvailabilityStatus;
  roas: ReportMetricAvailabilityStatus;
}

type ReportingDataset = ReportQueryRequest['dataset'];

function publicationStatus(
  freshness: ReportFreshness | undefined,
  isComplete: boolean | null | undefined,
  asOf: string | null | undefined,
  publishedDays: readonly string[] | undefined,
): ReportMetricAvailabilityStatus {
  if (!freshness || isComplete === undefined) return 'unknown';
  if (isComplete === null) return 'unpublished';
  if (isComplete) return 'published';
  return asOf || publishedDays?.length ? 'published' : 'unpublished';
}

/**
 * Canonical facts use null completeness to mean that no publication covers the
 * requested window. A false completeness value remains renderable when the
 * report has publication evidence from either its as-of timestamp or per-day
 * manifest. The manifest remains the stricter signal for chart zero-filling;
 * report-level availability must not mistake an empty settled-day manifest for
 * an unpublished provisional report.
 */
export function reportingMetricAvailability(
  dataset: ReportingDataset,
  freshness: ReportFreshness | undefined,
): ReportMetricAvailability {
  const delivery = dataset === 'attribution'
    ? 'unsupported'
    : publicationStatus(
        freshness,
        freshness?.delivery_is_complete,
        freshness?.delivery_as_of,
        freshness?.delivery_published_days,
      );
  const attribution = dataset === 'delivery'
    ? 'unsupported'
    : publicationStatus(
        freshness,
        freshness?.attribution_is_complete,
        freshness?.attribution_as_of,
        freshness?.attribution_published_days,
      );

  let roas: ReportMetricAvailabilityStatus = 'unsupported';
  if (dataset === 'performance') {
    if (delivery === 'published' && attribution === 'published') {
      roas = 'published';
    } else if (delivery === 'unpublished' || attribution === 'unpublished') {
      roas = 'unpublished';
    } else {
      roas = 'unknown';
    }
  }

  return { delivery, attribution, roas };
}

export function reportMetricIsAvailable(
  availability: ReportMetricAvailability,
  family: ReportMetricFamily,
): boolean {
  return availability[family] === 'published';
}

export function reportMetricUnavailableReason(
  availability: ReportMetricAvailability,
  family: ReportMetricFamily,
): string {
  const status = availability[family];
  if (status === 'unsupported') {
    if (family === 'delivery') {
      return 'Attributed-sales facts do not contain media delivery metrics.';
    }
    if (family === 'attribution') {
      return 'Delivery facts do not contain attributed sales metrics.';
    }
    return 'ROAS requires combined delivery spend and attributed revenue.';
  }
  if (status === 'unpublished') {
    if (family === 'delivery') {
      return 'No published delivery snapshot covers this scope and date range.';
    }
    if (family === 'attribution') {
      return 'No published attribution run covers this scope and date range.';
    }
    return 'ROAS is unavailable until both delivery and attribution publications cover this scope and date range.';
  }
  if (status === 'unknown') {
    return 'Publication availability has not been resolved yet.';
  }
  return '';
}

/**
 * Why today's TTD figures may be zero, for a range that includes today.
 * Current-day TTD delivery arrives through a separately gated lane
 * (`delivery_reporting.ttd_intraday_mode`); `off` and `shadow` write nothing,
 * and a zero from either looks exactly like a zero from a quiet day. Returns
 * null when the lane publishes, or when the response did not report the mode.
 */
export function currentDayTtdLaneNote(freshness: ReportFreshness | undefined): string | null {
  const mode = freshness?.ttd_intraday_mode ?? null;
  if (mode === null || mode === 'publish') return null;
  const state = mode === 'shadow'
    ? 'runs in shadow mode (validated, not published)'
    : 'is off';
  return `Current-day TTD delivery ${state}, so today\u2019s TTD spend, impressions and clicks read as zero; an administrator sets Ttd intraday mode to publish under Admin \u203a Config \u203a Delivery reporting. Meta\u2019s current day is unaffected.`;
}
